import { Box, Paper, Typography, TextField, IconButton, Stack, Divider, Avatar, Accordion, AccordionSummary, AccordionDetails, Link as MuiLink, List, ListItem, ListItemText, InputAdornment, ClickAwayListener, ListItemButton, Button, Tabs, Tab } from "@mui/material";
import { ScreenGreeting } from "../../../Components/Molecules";
import SendIcon from "@mui/icons-material/Send";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonIcon from "@mui/icons-material/Person";
import ScheduleIcon from "@mui/icons-material/Schedule";
import React from "react";
import api from "../../../Services/api";
import Tooltip from "../../../Components/Atoms/Tooltip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { Link as RouterLink } from "react-router-dom";
import { useSupport } from "../../../Contexts/SupportContext";

export const Support = () => {
  const [message, setMessage] = React.useState("");
  const [messages, setMessages] = React.useState<Array<{ date: string; message: string; user: string }>>([]);
  const [faqQuery, setFaqQuery] = React.useState("");

  const [sending, setSending] = React.useState(false);
  const [tab, setTab] = React.useState(0); // 0 = FAQ (default), 1 = Chat
  const { unread, refreshUnread } = useSupport();
  const [showChatBadge, setShowChatBadge] = React.useState(false);

  React.useEffect(() => {
    // Refresh unread count when entering the support page, no toast here
    refreshUnread({ notify: false }).then((count) => {
      if (typeof count === 'number') {
        setShowChatBadge(count > 0);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setShowChatBadge(unread > 0);
  }, [unread]);

  const customerQuestions = React.useMemo(
    () => [
      "Como posso antecipar minhas notas fiscais?",
      "Vocês oferecem antecipação de folha de pagamento?",
      "Estou com problemas ao tentar fazer um Pix, podem me ajudar?",
      "Como faço uma transferência P2P?",
      "Não consigo gerar meu token de autenticação. O que devo fazer?",
      "Estou tendo problemas para fazer login na minha conta.",
      "Quais documentos são necessários para antecipar notas fiscais?",
      "A confirmação da minha transferência P2P ainda não chegou. Isso é normal?",
      "Quais são os requisitos para antecipação de folha de pagamento?",
      "O sistema de Pix está fora do ar?",
      "Como posso redefinir minha senha?",
      "Quais são os horários de atendimento ao cliente?",
      "Há alguma atualização sobre meu problema?",
      "Como posso entrar em contato com o suporte?",
      "O que devo fazer se meu aplicativo travar?",
      
      "Onde vejo um resumo do meu saldo e movimentações?",
      "Como consultar meu extrato por período?",
      "Como filtrar por tipo de transação?",
      "Como exportar o extrato (CSV)?",
      
      "Como faço uma transferência Pix?",
      "Como pagar com Pix copia e cola?",
      "Onde acompanho minhas vendas Pix?",
      "Há limites e prazos no Pix?",
      "Onde vejo detalhes de um Pix específico?",
      "Como acessar a área Pix rapidamente?",
      
      "Como realizar uma transferência?",
      "Posso agendar transferências?",
      
      "Como pagar um boleto?",
      "Quais os prazos de compensação de boletos?",
      
      "Como criar um novo boleto?",
      "Onde vejo meus boletos?",
      "É possível cancelar um boleto emitido?",
      
      "Como acessar ou baixar meu comprovante?",
      "Onde vejo estornos?",
      
      "Como acessar a Folha de Pagamento?",
      "Como ver detalhes de um arquivo de folha?",
      
      "Onde gerencio meus pontos de venda (PVs)?",
      "Como ver detalhes de um ponto de venda?",
      
      "Como listar e criar operadores?",
      
      "Como acessar a área de antecipação?",
      "Como adicionar notas fiscais para antecipação?",
      "Onde vejo Compradores?",
      "Onde vejo Duplicatas?",
      
      "Onde altero meus dados do perfil?",
      "Onde vejo meu cartão?",
      
      "Como acessar a Pagadoria?",
      "Como ver detalhes de um pagamento na Pagadoria?",
    ],
    []
  );
  const [suggestionsOpen, setSuggestionsOpen] = React.useState(false);
  const [highlightIndex, setHighlightIndex] = React.useState<number>(-1);
  const inputWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const inputFocusedRef = React.useRef<boolean>(false);

  // FAQ compartilhado entre a listagem e a checagem de correspondência exata
  type FaqItem = { question: string; to?: string; secondary?: string };
  type FaqCat = { title: string; description?: string; items: FaqItem[] };
  const faqData: FaqCat[] = React.useMemo(
    () => [
      {
        title: "Início (Dashboard)",
        description: "Visão geral de saldos e movimentações, com atalhos para áreas principais.",
        items: [
          { question: "Onde vejo um resumo do meu saldo e movimentações?", to: "/", secondary: "Acesse a tela Início" },
        ],
      },
      {
        title: "Extrato",
        description: "Consulte lançamentos por período e tipo.",
        items: [
          { question: "Como consultar meu extrato por período?", to: "/extrato", secondary: "Ir para Extrato" },
          { question: "Como filtrar por tipo de transação?", to: "/extrato", secondary: "Use os filtros disponíveis na tela" },
          { question: "Como exportar o extrato (CSV)?", to: "/extrato", secondary: "Opção de exportação quando disponível" },
        ],
      },
      {
        title: "Área Pix",
        description: "Central para transferir, pagar via copia-e-cola e acompanhar vendas Pix.",
        items: [
          { question: "Como faço uma transferência Pix?", to: "/pix/transferencia", secondary: "Transferência Pix" },
          { question: "Como pagar com Pix copia e cola?", to: "/pix/copia-cola", secondary: "Pix copia e cola" },
          { question: "Onde acompanho minhas vendas Pix?", to: "/pix/vendas", secondary: "Vender (Vendas Pix)" },
          { question: "Há limites e prazos no Pix?", to: "/pix", secondary: "Verifique as regras exibidas na área Pix" },
          { question: "Onde vejo detalhes de um Pix específico?", secondary: "Abra a área Pix e selecione o item desejado" },
          { question: "Acesso rápido à área Pix", to: "/pix", secondary: "Ir para Área Pix" },
        ],
      },
      {
        title: "Transferências",
        description: "Envie valores e acompanhe o status.",
        items: [
          { question: "Como realizar uma transferência?", to: "/transferencia", secondary: "Ir para Transferências" },
          { question: "Posso agendar transferências?", to: "/transferencia", secondary: "Verifique as opções disponíveis na tela" },
        ],
      },
      {
        title: "Pagamentos (Boletos)",
        description: "Pague boletos e acompanhe o processamento.",
        items: [
          { question: "Como pagar um boleto?", to: "/pagamento", secondary: "Ir para Pagamentos" },
          { question: "Quais os prazos de compensação?", to: "/pagamento", secondary: "Informações exibidas durante a confirmação do pagamento" },
        ],
      },
      {
        title: "Boletos (Emissão)",
        description: "Gere e gerencie boletos emitidos.",
        items: [
          { question: "Como criar um novo boleto?", to: "/boletos/add", secondary: "Criar Boleto" },
          { question: "Onde vejo meus boletos?", to: "/boletos", secondary: "Listagem de Boletos" },
          { question: "É possível cancelar um boleto emitido?", to: "/boletos", secondary: "Verifique as ações disponíveis na listagem" },
        ],
      },
      {
        title: "Comprovantes e Estornos",
        description: "Acesse comprovantes e visualize estornos.",
        items: [
          { question: "Como acessar ou baixar meu comprovante?", to: "/comprovantes", secondary: "Ir para Comprovantes" },
          { question: "Onde vejo estornos?", to: "/estornos", secondary: "Ir para Estornos" },
        ],
      },
      {
        title: "Folha de Pagamento",
        description: "Importe, acompanhe e detalhe arquivos.",
        items: [
          { question: "Como acessar a Folha de Pagamento?", to: "/payroll", secondary: "Ir para Folha de Pagamento" },
          { question: "Como ver detalhes de um arquivo?", secondary: "Abra a lista e selecione o arquivo desejado" },
        ],
      },
      {
        title: "Pontos de Venda (PV)",
        description: "Gerencie seus pontos de venda.",
        items: [
          { question: "Onde gerencio meus PVs?", to: "/pontos-de-venda", secondary: "Ir para Pontos de Venda" },
          { question: "Como ver detalhes de um PV?", secondary: "Acesse a lista e selecione um PV" },
        ],
      },
      {
        title: "Operadores",
        description: "Crie e gerencie usuários operadores.",
        items: [
          { question: "Como listar e criar operadores?", to: "/operadores", secondary: "Ir para Operadores" },
        ],
      },
      {
        title: "Antecipação",
        description: "Solicite antecipação e gerencie documentos.",
        items: [
          { question: "Como acessar antecipação?", to: "/anticipation", secondary: "Ir para Antecipação" },
          { question: "Adicionar notas fiscais", to: "/anticipation/addInvoice", secondary: "Adicionar Notas Fiscais" },
          { question: "Compradores e Duplicatas", secondary: "Acesse as seções de Compradores e Duplicatas" },
          { question: "Onde vejo Compradores?", to: "/anticipation/buyers", secondary: "Compradores" },
          { question: "Onde vejo Duplicatas?", to: "/anticipation/duplicates", secondary: "Duplicatas" },
        ],
      },
      {
        title: "Meu Perfil e Cartões",
        description: "Atualize seus dados e gerencie seu cartão.",
        items: [
          { question: "Onde altero meus dados?", to: "/perfil", secondary: "Ir para Meu Perfil" },
          { question: "Onde vejo meu cartão?", to: "/meus-cartoes", secondary: "Meu Cartão" },
        ],
      },
      {
        title: "Pagadoria",
        description: "Gestão de pagamentos em lote e detalhes.",
        items: [
          { question: "Como acessar a Pagadoria?", to: "/pay-office", secondary: "Ir para Pagadoria" },
          { question: "Como ver detalhes de um pagamento?", secondary: "Abra a lista e selecione o pagamento desejado" },
        ],
      },
    ],
    []
  );

  // Utilitários para normalização e similaridade (aprox. 95%)
  const normalize = React.useCallback((s: string) => {
    return s
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // remove acentos
      .replace(/[^a-z0-9\s]/g, " ") // remove pontuação
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  const levenshtein = React.useCallback((a: string, b: string) => {
    const m = a.length;
    const n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;
    const dp = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
      const ca = a.charCodeAt(i - 1);
      for (let j = 1; j <= n; j++) {
        const cb = b.charCodeAt(j - 1);
        const cost = ca === cb ? 0 : 1;
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + cost
        );
      }
    }
    return dp[m][n];
  }, []);

  const similarity = React.useCallback((a: string, b: string) => {
    const na = normalize(a);
    const nb = normalize(b);
    if (!na || !nb) return 0;
    // bônus: se um contém o outro e tem tamanho razoável
    if (na.length >= 8 && (na.includes(nb) || nb.includes(na))) return 1;
    const dist = levenshtein(na, nb);
    const maxLen = Math.max(na.length, nb.length) || 1;
    return 1 - dist / maxLen;
  }, [levenshtein, normalize]);

  const [faqSuggestion, setFaqSuggestion] = React.useState<{ cat: FaqCat; item: FaqItem; score: number } | null>(null);
  const faqCardRef = React.useRef<HTMLDivElement | null>(null);
  const chatEndRef = React.useRef<HTMLDivElement | null>(null);

  const THRESHOLD = 0.95;

  const computeBestFaq = React.useCallback(
    (content: string): { cat: FaqCat; item: FaqItem; score: number } | null => {
      const q = content.trim();
      if (!q) return null;
      let best: { cat: FaqCat; item: FaqItem; score: number } | null = null;
      for (const cat of faqData) {
        for (const item of cat.items) {
          const sc = similarity(q, item.question);
          if (!best || sc > best.score) best = { cat, item, score: sc };
        }
      }
      return best;
    },
    [faqData, similarity]
  );

  React.useEffect(() => {
    const best = computeBestFaq(message);
    setFaqSuggestion(best && best.score >= THRESHOLD ? best : null);
  }, [message, computeBestFaq]);

  const listRef = React.useRef<HTMLDivElement | null>(null);
  const didInitialAutoScroll = React.useRef(false);
  const NEAR_BOTTOM_PCT = 0.3; // 30% of container height
  const ANIMATION_DURATION_MS = 1000; // 1 second

  const formatDateTimeBR = React.useCallback((value: string) => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    const now = new Date();
    const diffMs = Math.abs(now.getTime() - d.getTime());
    if (diffMs < 60 * 1000) return "Agora";
    const isSameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    if (isSameDay) {
      return new Intl.DateTimeFormat("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(d);
    }
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  }, []);

  const scrollToBottom = React.useCallback(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, []);

  const animateScrollToBottom = React.useCallback((duration = ANIMATION_DURATION_MS) => {
    const el = listRef.current;
    if (!el) return;
    const start = el.scrollTop;
    const end = el.scrollHeight - el.clientHeight;
    if (end <= start) return;

    const prefersReduced = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced || duration <= 0) {
      el.scrollTop = end;
      return;
    }

    const startTime = performance.now();
    const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeInOut(progress);
      el.scrollTop = start + (end - start) * eased;
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, []);

  const isNearBottom = React.useCallback(() => {
    const el = listRef.current;
    if (!el) return false;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const thresholdPx = el.clientHeight * NEAR_BOTTOM_PCT;
    return distanceFromBottom <= thresholdPx;
  }, []);

  const apiFetch = React.useCallback(async (opts?: { reason?: "initial" | "send" | "poll" }) => {
    const reason = opts?.reason ?? "poll";
    const wasNearBottom = isNearBottom();
    try {
      const { data } = await api.get("/support");
      if (Array.isArray(data)) {
        setMessages(data);
        if (reason === "initial" && !didInitialAutoScroll.current) {
          setTimeout(() => {
            animateScrollToBottom(ANIMATION_DURATION_MS);
            didInitialAutoScroll.current = true;
          }, 0);
        } else if (reason === "send") {
          setTimeout(() => animateScrollToBottom(ANIMATION_DURATION_MS), 0);
        } else if (reason === "poll" && wasNearBottom) {
          setTimeout(() => animateScrollToBottom(ANIMATION_DURATION_MS), 0);
        }
      }
    } catch (e) {
      // silent
    }
  }, [isNearBottom, animateScrollToBottom]);

  React.useEffect(() => {
    apiFetch({ reason: "initial" });
    const id = setInterval(() => apiFetch({ reason: "poll" }), 10000);
    return () => clearInterval(id);
  }, [apiFetch]);

  // Ao entrar na aba de Chat, rola para o final da lista
  React.useEffect(() => {
    if (tab === 1) {
      setTimeout(() => {
        // rola lista interna do chat
        animateScrollToBottom(ANIMATION_DURATION_MS);
        // rola o body da página até o fim
        try {
          const top = Math.max(
            document.documentElement?.scrollHeight || 0,
            document.body?.scrollHeight || 0
          );
          window.scrollTo({ top, behavior: 'smooth' });
        } catch {}
        // garante que a área final do chat fique visível no contêiner principal
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 0);
    }
  }, [tab, animateScrollToBottom]);

  const handleSend = async (opts?: { bypassFaqCheck?: boolean }) => {
    const content = message.trim();
    if (!content) return;

    // Checagem síncrona do FAQ para evitar corrida
    if (!opts?.bypassFaqCheck) {
      const best = computeBestFaq(content);
      if (best && best.score >= THRESHOLD) {
        setFaqSuggestion(best);
        return;
      }
    }

    try {
      setSending(true);
      await api.post("/support", content, { headers: { "Content-Type": "text/plain" } });
      setMessage("");
      await apiFetch({ reason: "send" });
    } catch (e) {
      // Optionally handle toast via hook if configured
    } finally {
      setSending(false);
    }
  };

  const filteredSuggestions = React.useMemo(() => {
    const q = message.trim().toLowerCase();
    let list = !q
      ? customerQuestions
      : customerQuestions.filter((s) => s.toLowerCase().includes(q));
    // limitar quantidade para não poluir a tela
    if (list.length > 8) list = list.slice(0, 8);
    return list;
  }, [customerQuestions, message]);

  const openSuggestionsIfNeeded = React.useCallback(() => {
    const shouldOpen = inputFocusedRef.current && filteredSuggestions.length > 0;
    setSuggestionsOpen(shouldOpen);
    if (!shouldOpen) setHighlightIndex(-1);
  }, [filteredSuggestions.length]);

  const handleSelectSuggestion = React.useCallback(
    (value: string) => {
      setMessage(value);
      setSuggestionsOpen(false);
      setHighlightIndex(-1);
      const best = computeBestFaq(value);
      if (best && best.score >= THRESHOLD) {
        setFaqSuggestion(best);
        setTimeout(() => {
          faqCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 0);
      }
    },
    [computeBestFaq]
  );

  React.useEffect(() => {
    if (faqSuggestion && tab === 1) {
      setTimeout(() => {
        faqCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 0);
    }
  }, [faqSuggestion, tab]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestionsOpen && filteredSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex((prev) => {
          const next = prev < filteredSuggestions.length - 1 ? prev + 1 : 0;
          return next;
        });
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex((prev) => {
          const next = prev > 0 ? prev - 1 : filteredSuggestions.length - 1;
          return next;
        });
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setSuggestionsOpen(false);
        setHighlightIndex(-1);
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        const chosen =
          highlightIndex >= 0
            ? filteredSuggestions[highlightIndex]
            : filteredSuggestions[0];
        if (chosen) {
          handleSelectSuggestion(chosen);
          return;
        }
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      // Intercepta envio se houver sugestão do FAQ visível
      if (faqSuggestion) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <ScreenGreeting title={"Suporte"} subTitle={"Como podemos ajudar?"} />
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            if (v === 1) setShowChatBadge(false);
          }}
          aria-label="Abas de suporte"
        >
          <Tab label="Ajuda (FAQ)" />
          <Tab
            label={
              <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                Chat - Fale com o suporte
                {showChatBadge && unread > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -6,
                      right: -16,
                      backgroundColor: 'error.main',
                      color: 'common.white',
                      borderRadius: 999,
                      fontSize: 10,
                      fontWeight: 700,
                      px: 0.75,
                      minWidth: 16,
                      height: 16,
                      lineHeight: '16px',
                      textAlign: 'center',
                    }}
                  >
                    {unread}
                  </Box>
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {tab === 1 && (
      <>
      <Paper elevation={3}>
        <Box p={2} display="flex" alignItems="center" gap={1}>
          <ChatBubbleOutlineIcon color="primary" />
          <Typography variant="body2">
            Ficaremos felizes em respondê-lo. Envie sua mensagem abaixo.
          </Typography>
          <Tooltip title="Informações sobre o suporte">
            <IconButton
              size="small"
              aria-label="Informações sobre o suporte"
              onClick={() => window.alert("Nosso suporte trabalha em horário comercial de brasilia")}
            >
              <InfoOutlinedIcon color="action" fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />
        <Box ref={listRef} sx={{ maxHeight: 400, minHeight: 200, overflowY: "auto", p: 2, backgroundColor: "#fafafa" }}>
          <Stack spacing={1.5}>
            {messages.map((m, idx) => {
              const isMe = m.user?.toLowerCase() === "você";
              return (
                <Box key={idx} display="flex" justifyContent={isMe ? "flex-end" : "flex-start"}>
                  <Box maxWidth="75%" display="flex" gap={1} alignItems="flex-start">
                    {!isMe && (
                      <Avatar sx={{ width: 24, height: 24 }}>
                        <PersonIcon fontSize="small" />
                      </Avatar>
                    )}
                    <Box
                      sx={{
                        p: 1.2,
                        borderRadius: 2,
                        backgroundColor: isMe ? "primary.main" : "grey.200",
                        color: isMe ? "common.white" : "text.primary",
                      }}
                    >
                      <Typography variant="body2" color="inherit" sx={{ whiteSpace: "pre-wrap" }}>{m.message}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, opacity: 0.75 }}>
                        <ScheduleIcon sx={{ fontSize: 14 }} />
                        <Typography variant="caption" color="inherit">{formatDateTimeBR(m.date)}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
        <Divider />
        <Box p={2} display="flex" gap={1} alignItems="flex-end">
          <ClickAwayListener
            onClickAway={() => {
              setSuggestionsOpen(false);
              setHighlightIndex(-1);
            }}
          >
            <Box ref={inputWrapperRef} sx={{ position: "relative", flex: 1 }}>
              <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={6}
                placeholder="Escreva sua mensagem..."
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  // reabre/atualiza conforme a digitação
                  setTimeout(openSuggestionsIfNeeded, 0);
                }}
                onFocus={() => {
                  inputFocusedRef.current = true;
                  openSuggestionsIfNeeded();
                }}
                onBlur={() => {
                  inputFocusedRef.current = false;
                }}
                onKeyDown={handleKeyDown}
              />
              {suggestionsOpen && filteredSuggestions.length > 0 && (
                <Paper
                  elevation={4}
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: "100%",
                    mb: 1,
                    maxHeight: 240,
                    overflowY: "auto",
                    zIndex: 5,
                  }}
                >
                  <List dense disablePadding>
                    {filteredSuggestions.map((s, idx) => (
                      <ListItemButton
                        key={idx}
                        selected={idx === highlightIndex}
                        onMouseEnter={() => setHighlightIndex(idx)}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSelectSuggestion(s)}
                      >
                        <ListItemText primary={s} />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          </ClickAwayListener>
          <Tooltip title="Enviar mensagem">
            <span>
              <IconButton color="primary" onClick={() => handleSend()} disabled={!message.trim() || sending}>
                <SendIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {faqSuggestion && (
          <Box ref={faqCardRef} px={2} pb={2}>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: (t) => t.palette.mode === 'light' ? '#fff' : 'background.paper' }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">Sugestão do FAQ</Typography>
                <Typography variant="body1">{faqSuggestion.item.question}</Typography>
                {faqSuggestion.item.secondary && (
                  <Typography variant="body2" color="text.secondary">{faqSuggestion.item.secondary}</Typography>
                )}
                {faqSuggestion.item.to && (
                  <MuiLink
                    component={RouterLink}
                    to={faqSuggestion.item.to}
                    underline="hover"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      transition: 'transform 120ms ease',
                      '&:hover': { transform: 'translateX(-2px) !important' },
                    }}
                  >
                    Abrir seção: {faqSuggestion.cat.title}
                  </MuiLink>
                )}
                {!faqSuggestion.item.to && !faqSuggestion.item.secondary && (
                  <Typography variant="body2" color="text.secondary">Verifique a seção correspondente no menu: {faqSuggestion.cat.title}</Typography>
                )}
                <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                  <Button onClick={() => { setMessage(""); setFaqSuggestion(null); }}>Foi útil</Button>
                  <Tooltip title="envie agora a pergunta ao suporte">
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SendIcon fontSize="small" sx={{ color: 'common.white' }} />}
                        onClick={() => handleSend({ bypassFaqCheck: true })}
                        sx={{ color: 'common.white' }}
                      >
                        Enviar ao suporte
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            </Paper>
          </Box>
        )}
      </Paper>
      <Box ref={chatEndRef} />
      </>
      )}

      {tab === 0 && (
      <Box mt={1}>
        <Box mb={2}>
          <Button
            variant="contained"
            color="primary"
            sx={{ color: 'common.white' }}
            startIcon={<ChatBubbleOutlineIcon sx={{ color: 'common.white' }} />}
            onClick={() => setTab(1)}
          >
            Falar com o suporte
          </Button>
        </Box>
        <Typography variant="h6" gutterBottom>FAQ - Dúvidas Frequentes</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Encontre respostas rápidas e acesse diretamente as telas principais.
        </Typography>

        <Box mb={2} maxWidth={520}>
          <TextField
            fullWidth
            size="small"
            label="Buscar no FAQ"
            value={faqQuery}
            onChange={(e) => setFaqQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: faqQuery ? (
                <InputAdornment position="end">
                  <IconButton aria-label="Limpar busca" size="small" onClick={() => setFaqQuery("")}> 
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>

        {(() => {
          const q = faqQuery.trim().toLowerCase();
          const filtered = !q
            ? faqData
            : faqData
                .map((cat) => {
                  const matchesCat = [cat.title, cat.description || ""].some((s) => s.toLowerCase().includes(q));
                  const items = cat.items.filter((it) => [it.question, it.secondary || ""].some((s) => s.toLowerCase().includes(q)));
                  return matchesCat ? cat : { ...cat, items };
                })
                .filter((cat) => cat.items.length > 0 || (cat.description && cat.description.toLowerCase().includes(q)) || cat.title.toLowerCase().includes(q));

          if (filtered.length === 0) {
            return (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2">Nenhum resultado encontrado para "{faqQuery}".</Typography>
              </Paper>
            );
          }

          return (
            <Stack spacing={1}>
              {filtered.map((cat, idx) => (
                <Accordion key={idx}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>{cat.title}</AccordionSummary>
                  <AccordionDetails>
                    {cat.description && (
                      <Typography variant="body2" sx={{ mb: 1 }}>{cat.description}</Typography>
                    )}
                    <List dense>
                      {cat.items.map((it, k) => (
                        <ListItem key={k}>
                          {it.to ? (
                            <ListItemText
                              primary={it.question}
                              secondary={<MuiLink component={RouterLink} to={it.to}>{it.secondary || "Abrir"}</MuiLink>}
                            />
                          ) : (
                            <ListItemText primary={it.question} secondary={it.secondary} />
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          );
        })()}
      </Box>
      )}
    </>
  );
};

export default Support;
